import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { CALENDLY_LINK } from '../../utils/constants';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', path: '/', icon: 'Home' },
    { name: 'Services', path: '/services', icon: 'Briefcase' },
    { name: 'Business Valuation', path: '/valuation', icon: 'TrendingUp' },
    { name: 'About', path: '/about', icon: 'Users' },
    // { name: 'Case Studies', path: '/case-studies', icon: 'FileText' }
  ];

  const moreItems = [
    // { name: 'Litigation Support', path: '/litigation-support', icon: 'Scale' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-background/95 backdrop-blur-md shadow-brand border-b border-border'
        : 'bg-background/80 backdrop-blur-sm'
        }`}>
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-6 lg:px-8">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
              onClick={closeMenu}
            >
              <img
                src="/Logo.png"
                alt="Northbridge Valuation & Advisory"
                className="w-[200px] h-auto"
                loading="eager"
                decoding="async"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-muted ${isActivePath(item?.path)
                    ? 'text-accent bg-accent/10' : 'text-text-primary hover:text-accent'
                    }`}
                >
                  {item?.name}
                </Link>
              ))}

              {/* More Dropdown */}
              <div className="relative group">
                {/* <button className="flex items-center px-4 py-2 rounded-lg text-sm font-medium text-text-primary hover:text-accent hover:bg-muted transition-all duration-200">
                  More
                  <Icon name="ChevronDown" size={16} className="ml-1 transition-transform duration-200 group-hover:rotate-180" />
                </button> */}

                <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-brand-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                  <div className="py-2">
                    {moreItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`flex items-center px-4 py-2 text-sm font-medium transition-colors duration-200 hover:bg-muted ${isActivePath(item?.path)
                          ? 'text-accent bg-accent/10' : 'text-text-primary hover:text-accent'
                          }`}
                      >
                        <Icon name={item?.icon} size={16} className="mr-3" />
                        {item?.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* CTA Button & Mobile Menu Toggle */}
            <div className="flex items-center space-x-4">
              <Button
                variant="default"
                size="sm"
                className="hidden sm:flex"
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                asChild
              >
                <Link to="/book-consultation">
                  Free Consultation
                </Link>
              </Button>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMenu}
                className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-200"
                aria-label="Toggle menu"
              >
                <Icon
                  name={isMenuOpen ? "X" : "Menu"}
                  size={24}
                  className="text-text-primary"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 ease-out ${isMenuOpen
          ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}>
          <div className="px-6 py-4 bg-background/95 backdrop-blur-md border-t border-border">
            <nav className="space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${isActivePath(item?.path)
                    ? 'text-accent bg-accent/10' : 'text-text-primary hover:text-accent hover:bg-muted'
                    }`}
                >
                  <Icon name={item?.icon} size={18} className="mr-3" />
                  {item?.name}
                </Link>
              ))}

              {moreItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${isActivePath(item?.path)
                    ? 'text-accent bg-accent/10' : 'text-text-primary hover:text-accent hover:bg-muted'
                    }`}
                >
                  <Icon name={item?.icon} size={18} className="mr-3" />
                  {item?.name}
                </Link>
              ))}
            </nav>

            <div className="mt-6 pt-4 border-t border-border">
              <Button
                variant="default"
                size="sm"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                onClick={closeMenu}
                asChild
              >
                <Link to="/book-consultation">
                  Free Consultation
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>
      {/* Spacer to prevent content overlap */}
      <div className="h-16"></div>
      {/* Floating Credential Badge - appears after scroll */}
      {isScrolled && (
        <div className="fixed top-20 right-6 z-40 animate-slide-up">
          <div className="bg-success/10 border border-success/20 rounded-full px-3 py-1 backdrop-blur-sm">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse-subtle"></div>
              <span className="text-xs font-medium text-success">CVA & CPA Certified</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;